import React from "react";
import Seo from "../components/common/Seo";
import LegalLayout, {
  LegalSection,
  LegalSubheading,
  LegalText,
  LegalList,
  LegalContact,
} from "../components/common/LegalLayout";

const EMAIL = "f_uo@infiniteloop-tech.com";

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Seo
        title="Privacy policy | Fork"
        description="How Fork collects, uses and safeguards your information — the data we store, who we share it with, and how to request deletion."
        path="/privacy"
      />
      <LegalLayout
        title="Privacy policy"
        lastUpdated="Last updated: December 13, 2025"
        otherHref="/terms"
        otherLabel="Terms & conditions"
        lede={
          <>
            Infinite Loop Tech, Inc. (the "Company") is committed to maintaining
            robust privacy protections for its users. This Privacy Policy is
            designed to help you understand how we collect, use and safeguard
            the information you provide to us.
          </>
        }
        intro={
          <>
            For purposes of this Agreement, "Site" refers to the Company's
            website. "Service" refers to the Company's services accessed via the
            Site and our mobile application,{" "}
            <strong className="text-white">Fork AI</strong>, in which users can
            track their nutrition by taking pictures or recording their voice
            about what they eat. The terms "we," "us," and "our" refer to the
            Company. "You" refers to you, as a user of our Site or our Service.
            By accessing our Site or our Service, you accept our Privacy Policy
            and Terms of Use, and you consent to our collection, storage, use
            and disclosure of your Personal Information as described in this
            Privacy Policy.
          </>
        }
      >
        <LegalSection label="I." title="Information we collect">
          <LegalText>
            We collect "Non-Personal Information" and "Personal Information."
            Non-Personal Information includes information that cannot be used to
            personally identify you, such as anonymous usage data, general
            demographic information we may collect, referring/exit pages and
            URLs, platform types, preferences you submit and preferences that
            are generated based on the data you submit and number of clicks.
          </LegalText>
          <LegalText>
            Personal Information includes your email, photos of food, voice
            recordings, and nutrition data, which you submit to us through the
            registration process at the Site.
          </LegalText>

          <LegalSubheading>
            1. Information collected via technology
          </LegalSubheading>
          <LegalText>
            To activate the Service you do not need to submit any Personal
            Information other than your email address. To use the Service
            thereafter, you do need to submit further Personal Information,
            which includes photos of meals and voice logs uploaded for analysis.
          </LegalText>
          <LegalText>
            In an effort to improve the quality of the Service, we track
            information provided to us by your browser or by our software
            application when you view or use the Service, such as the website
            you came from (known as the "referring URL"), the type of browser
            you use, the device from which you connected to the Service, the
            time and date of access, and other information that does not
            personally identify you.
          </LegalText>
          <LegalText>
            We track this information using cookies — small text files which
            include an anonymous unique identifier, sent to your browser from
            our servers and stored on your device. Cookies let us collect
            Non-Personal Information about that user and keep a record of the
            user's preferences, both on an individual and aggregate basis. For
            example, the Company may use cookies (and device permissions) to
            collect the following information:
          </LegalText>
          <LegalList
            items={[
              "Device feature usage including Camera, Microphone, Lock Screen, and Dynamic Island interactions required for the App's functionality.",
            ]}
          />
          <LegalText last>
            The Company may use both persistent and session cookies; persistent
            cookies remain on your computer after you close your session and
            until you delete them, while session cookies expire when you close
            your browser.
          </LegalText>

          <LegalSubheading>
            2. Information you provide us by registering for an account
          </LegalSubheading>
          <LegalText last>
            In addition to the information provided automatically by your
            browser when you visit the Site, to become a subscriber to the
            Service you will need to create a personal profile. You can create a
            profile by registering with the Service and entering your email
            address, and creating a user name and a password. By registering,
            you are authorizing us to collect, store and use your email address
            in accordance with this Privacy Policy.
          </LegalText>

          <LegalSubheading>3. Children's privacy</LegalSubheading>
          <LegalText last>
            The Site and the Service are not directed to anyone under the age of
            13. The Site does not knowingly collect or solicit information from
            anyone under the age of 13, or allow anyone under the age of 13 to
            sign up for the Service. In the event that we learn that we have
            gathered personal information from anyone under the age of 13
            without the consent of a parent or guardian, we will delete that
            information as soon as possible. If you believe we have collected
            such information, please contact us at {EMAIL}.
          </LegalText>
        </LegalSection>

        <LegalSection label="II." title="How we use and share information">
          <LegalSubheading flush>Personal Information</LegalSubheading>
          <LegalText>
            Except as otherwise stated in this Privacy Policy, we do not sell,
            trade, rent or otherwise share for marketing purposes your Personal
            Information with third parties without your consent. We do share
            Personal Information with vendors who are performing services for
            the Company, such as cloud hosting providers and third-party AI
            providers (including public and private Large Language Models) who
            process your photos and voice recordings to generate nutrition
            insights. Those vendors use your Personal Information only at our
            direction and in accordance with our Privacy Policy.
          </LegalText>
          <LegalText>
            In general, the Personal Information you provide to us is used to
            help us communicate with you — to contact users in response to
            questions, solicit feedback, provide technical support, and inform
            users about promotional offers. We retain your Personal Information
            and uploaded content permanently unless you explicitly request
            deletion.
          </LegalText>
          <LegalText>
            We may share Personal Information with outside parties if we have a
            good-faith belief that access, use, preservation or disclosure of
            the information is reasonably necessary to meet any applicable legal
            process or enforceable governmental request; to enforce applicable
            Terms of Service, including investigation of potential violations;
            address fraud, security or technical concerns; or to protect against
            harm to the rights, property, or safety of our users or the public
            as required or permitted by law.
          </LegalText>

          <LegalSubheading>Non-Personal Information</LegalSubheading>
          <LegalText>
            In general, we use Non-Personal Information to help us improve the
            Service and customize the user experience. We also aggregate
            Non-Personal Information in order to track trends and analyze use
            patterns on the Site. This Privacy Policy does not limit in any way
            our use or disclosure of Non-Personal Information and we reserve the
            right to use and disclose such Non-Personal Information to our
            partners, advertisers and other third parties at our discretion.
          </LegalText>
          <LegalText last>
            In the event we undergo a business transaction such as a merger,
            acquisition by another company, or sale of all or a portion of our
            assets, your Personal Information may be among the assets
            transferred. You acknowledge and consent that such transfers may
            occur and are permitted by this Privacy Policy, and that any
            acquirer of our assets may continue to process your Personal
            Information as set forth in this Privacy Policy. If our information
            practices change at any time in the future, we will post the policy
            changes to the Site so that you may opt out of the new information
            practices. We suggest that you check the Site periodically if you
            are concerned about how your information is used.
          </LegalText>
        </LegalSection>

        <LegalSection label="III." title="How we protect information">
          <LegalText last>
            We implement security measures designed to protect your information
            from unauthorized access. Your account is protected by your account
            password and we urge you to take steps to keep your personal
            information safe by not disclosing your password and by logging out
            of your account after each use. We further protect your information
            from potential security breaches by implementing certain
            technological security measures including encryption, firewalls and
            secure socket layer technology. However, these measures do not
            guarantee that your information will not be accessed, disclosed,
            altered or destroyed by breach of such firewalls and secure server
            software. By using our Service, you acknowledge that you understand
            and agree to assume these risks.
          </LegalText>
        </LegalSection>

        <LegalSection
          label="IV."
          title="Your rights regarding the use of your Personal Information"
        >
          <LegalText>
            You have the right at any time to prevent us from contacting you for
            marketing purposes. When we send a promotional communication to a
            user, the user can opt out of further promotional communications by
            following the unsubscribe instructions provided in each promotional
            e-mail. You can also indicate that you do not wish to receive
            marketing communications from us in the Settings section of the App.
          </LegalText>
          <LegalText last>
            Please note that notwithstanding the promotional preferences you
            indicate by either unsubscribing or opting out in the Settings
            section of the Site, we may continue to send you administrative
            emails including, for example, periodic updates to our Privacy
            Policy.
          </LegalText>
        </LegalSection>

        <LegalSection label="V." title="Links to other websites">
          <LegalText last>
            As part of the Service, we may provide links to or compatibility
            with other websites or applications. However, we are not responsible
            for the privacy practices employed by those websites or the
            information or content they contain. This Privacy Policy applies
            solely to information collected by us through the Site and the
            Service. Therefore, this Privacy Policy does not apply to your use
            of a third party website accessed by selecting a link on our Site or
            via our Service. To the extent that you access or use the Service
            through or on another website or application, then the privacy
            policy of that other website or application will apply to your
            access or use of that site or application. We encourage our users to
            read the privacy statements of other websites before proceeding to
            use them.
          </LegalText>
        </LegalSection>

        <LegalSection label="VI." title="Changes to our Privacy Policy">
          <LegalText last>
            The Company reserves the right to change this policy and our Terms
            of Service at any time. We will notify you of significant changes to
            our Privacy Policy by sending a notice to the primary email address
            specified in your account or by placing a prominent notice on our
            site. Significant changes will go into effect 30 days following such
            notification. Non-material changes or clarifications will take
            effect immediately. You should periodically check the Site and this
            privacy page for updates.
          </LegalText>
        </LegalSection>

        <LegalContact label="VII." title="Contact us" email={EMAIL}>
          If you have any questions regarding this Privacy Policy or the
          practices of this Site, or if you wish to request the deletion of your
          account and data, please contact us by sending an email to:
        </LegalContact>
      </LegalLayout>
    </>
  );
};

export default PrivacyPolicy;
